import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import {
  clearPWAUpdateAvailableFlag,
  isPWAUpdateAvailableFlagSet,
  notifyPWAUpdateAvailable,
  PWA_UPDATE_AVAILABLE_EVENT,
} from './helpers'


const SC_UpdatePrompt = styled.div`
  position: fixed;
  bottom: 20px;
  left: 20px;
  right: 20px;
  background: #4CAF50;
  color: white;
  padding: 16px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 400px;
  margin: 0 auto;

  @media (max-width: 768px) {
    left: 10px;
    right: 10px;
    bottom: 10px;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
`

const SC_UpdateMessage = styled.div`
  flex: 1;
  margin-right: 16px;

  @media (max-width: 480px) {
    margin-right: 0;
  }
`

const SC_UpdateTitle = styled.div`
  font-weight: 600;
  margin-bottom: 4px;

  @media (max-width: 480px) {
    text-align: center;
  }
`

const SC_UpdateDescription = styled.div`
  font-size: 14px;
  opacity: 0.9;

  @media (max-width: 480px) {
    text-align: center;
  }
`

const SC_UpdateActions = styled.div`
  display: flex;
  gap: 8px;

  @media (max-width: 480px) {
    flex-direction: row;
    width: 100%;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
  }
`

const SC_UpdateButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }

  &.primary {
    background: white;
    color: #4CAF50;

    &:hover {
      background: rgba(255, 255, 255, 0.9);
    }
  }

  @media (max-width: 480px) {
    width: auto;
    min-width: 112px;
    padding: 6px 12px;
    font-size: 13px;
    white-space: nowrap;
  }
`

/**
 * Компонент для уведомления об обновлении PWA
 */
export const PWAUpdatePrompt: React.FC = () => {
  const [ showUpdate, setShowUpdate ] = useState(false)
  const [ registration, setRegistration ] = useState<ServiceWorkerRegistration | null>(null)

  useEffect(() => {
    const handleUpdateAvailable = () => {
      setShowUpdate(true)
    }

    const handleControllerChange = () => {
      clearPWAUpdateAvailableFlag()
    }

    if (isPWAUpdateAvailableFlagSet()) {
      setShowUpdate(true)
    }

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((reg) => {
        setRegistration(reg)

        if (reg.waiting) {
          notifyPWAUpdateAvailable()
        }

        reg.addEventListener('updatefound', () => {
          const newWorker = reg.installing
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                notifyPWAUpdateAvailable()
              }
            })
          }
        })
      })

      navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange)
    }

    window.addEventListener(PWA_UPDATE_AVAILABLE_EVENT, handleUpdateAvailable)

    return () => {
      window.removeEventListener(PWA_UPDATE_AVAILABLE_EVENT, handleUpdateAvailable)
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange)
      }
    }
  }, [])

  const handleUpdate = () => {
    const reloadWithCleanup = () => {
      clearPWAUpdateAvailableFlag()
      window.location.reload()
    }

    if (registration && registration.waiting) {
      let hasReloaded = false

      const safeReload = () => {
        if (hasReloaded) return
        hasReloaded = true
        reloadWithCleanup()
      }

      const fallbackTimeoutId = window.setTimeout(() => {
        safeReload()
      }, 3000)

      navigator.serviceWorker.addEventListener('controllerchange', () => {
        window.clearTimeout(fallbackTimeoutId)
        safeReload()
      }, { once: true })

      try {
        registration.waiting.postMessage({ type: 'SKIP_WAITING' })
      } catch {
        window.clearTimeout(fallbackTimeoutId)
        safeReload()
      }
    } else {
      reloadWithCleanup()
    }
  }

  const handleDismiss = () => {
    setShowUpdate(false)
  }

  if (!showUpdate) {
    return null
  }

  return (
    <SC_UpdatePrompt>
      <SC_UpdateMessage>
        <SC_UpdateTitle>Обновление доступно</SC_UpdateTitle>
        <SC_UpdateDescription>
          Новая версия приложения готова к использованию
        </SC_UpdateDescription>
      </SC_UpdateMessage>

      <SC_UpdateActions>
        <SC_UpdateButton onClick={handleDismiss}>
          Позже
        </SC_UpdateButton>
        <SC_UpdateButton className='primary' onClick={handleUpdate}>
          Обновить
        </SC_UpdateButton>
      </SC_UpdateActions>
    </SC_UpdatePrompt>
  )
}
